/**
 * A small, opinionated keyboard hook. Handlers get registered globally; if the
 * user is typing in an input, textarea, or contenteditable we let the event
 * through — except for `Escape` which is always global (needed to zoom out).
 *
 * Matching is done on `event.code` for letters (layout-independent) and
 * `event.key` for named keys. Modifiers are explicit: `mod` maps to cmd on
 * mac, ctrl elsewhere.
 */

import { useEffect } from 'react';

export interface Shortcut {
  /** e.g. "mod+s", "esc", "/", "shift+n" */
  combo: string;
  handler: (ev: KeyboardEvent) => void;
  /** If true, fire even when focus is in an editable field. Defaults to false. */
  allowInInputs?: boolean;
  /** If true (default), prevent default browser behavior when matched. */
  preventDefault?: boolean;
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

export function useShortcuts(shortcuts: readonly Shortcut[]): void {
  useEffect(() => {
    function onKey(ev: KeyboardEvent): void {
      // While a modal dialog is open, let the dialog handle keys itself.
      // Native <dialog showModal()> already closes on Escape; if we ALSO
      // handled Esc here, the editor would zoom out behind the dialog
      // simultaneously with the dialog dismissing. Same logic for the
      // other shortcuts — the dialog is the foreground task and owns the
      // keyboard until it's dismissed.
      if (isModalDialogOpen()) return;
      const editable = isEditable(ev.target);
      for (const s of shortcuts) {
        if (!matches(s.combo, ev)) continue;
        if (editable && !s.allowInInputs && s.combo !== 'esc' && s.combo !== 'escape') continue;
        if (s.preventDefault !== false) ev.preventDefault();
        s.handler(ev);
        return; // first match wins — prevents double-firing
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcuts]);
}

/**
 * True when any modal `<dialog>` is currently open. Used to suppress
 * global shortcuts so the dialog has full ownership of the keyboard
 * while it's the foreground surface.
 */
function isModalDialogOpen(): boolean {
  if (typeof document === 'undefined') return false;
  return document.querySelector('dialog[open]') !== null;
}

function matches(combo: string, ev: KeyboardEvent): boolean {
  const parts = combo
    .toLowerCase()
    .split('+')
    .map((p) => p.trim());
  const key = parts[parts.length - 1]!;
  const wantMod = parts.includes('mod');
  const wantShift = parts.includes('shift');
  const wantAlt = parts.includes('alt');

  const modPressed = IS_MAC ? ev.metaKey : ev.ctrlKey;
  if (wantMod !== modPressed) return false;
  if (wantShift !== ev.shiftKey) return false;
  if (wantAlt !== ev.altKey) return false;

  return keyMatches(key, ev);
}

function keyMatches(key: string, ev: KeyboardEvent): boolean {
  // Named keys
  if (key === 'esc' || key === 'escape') return ev.key === 'Escape';
  if (key === 'enter') return ev.key === 'Enter';
  if (key === 'space') return ev.key === ' ';
  if (key === 'tab') return ev.key === 'Tab';
  if (key === 'up') return ev.key === 'ArrowUp';
  if (key === 'down') return ev.key === 'ArrowDown';
  if (key === 'left') return ev.key === 'ArrowLeft';
  if (key === 'right') return ev.key === 'ArrowRight';
  if (key === '/') return ev.key === '/';
  if (key === '?') return ev.key === '?';
  // Single letters: use code for layout-independence (KeyS regardless of layout)
  if (/^[a-z]$/.test(key)) return ev.code === `Key${key.toUpperCase()}`;
  return ev.key.toLowerCase() === key;
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** Render the right modifier symbol for a given platform in UI hints. */
export function modKey(): string {
  return IS_MAC ? '⌘' : 'Ctrl';
}
